import { useState, useEffect, useCallback } from "react";
import { Box, Typography, Dialog, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  FunctionCall,
  BrowserUseSource,
} from "components/hooks/useTaskAgentState";
import { DefaultActionContainer } from "./DefaultActionDisplay";
import useIsMobile from "components/hooks/useIsMobile";

const getDomain = (url: string): string => {
  try {
    return new URL(url).hostname.replace("www.", "");
  } catch {
    return url;
  }
};

const StepThumbnail = ({
  source,
  onClick,
}: {
  source: BrowserUseSource;
  onClick: () => void;
}) => {
  const hasScreenshot = !!source.screenshot_url;
  return (
    <Box
      onClick={hasScreenshot ? onClick : undefined}
      sx={{
        cursor: hasScreenshot ? "pointer" : "default",
        borderRadius: "8px",
        border: "1px solid #e0e0e0",
        overflow: "hidden",
        width: 160,
        flexShrink: 0,
        "&:hover": hasScreenshot
          ? { borderColor: "#bdbdbd", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }
          : {},
      }}
    >
      {hasScreenshot ? (
        <Box
          sx={{
            width: "100%",
            height: 100,
            backgroundColor: "#f5f5f5",
            overflow: "hidden",
          }}
        >
          <img
            src={source.screenshot_url}
            alt={`Step ${source.step_number}`}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            height: 100,
            backgroundColor: "#f5f5f5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="bodySmall" color="text.secondary">
            No screenshot
          </Typography>
        </Box>
      )}
      <Box sx={{ padding: "6px 8px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            marginBottom: "2px",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#198282",
              color: "white",
              borderRadius: "4px",
              fontSize: "10px",
              fontWeight: 700,
              minWidth: "18px",
              height: "18px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {source.step_number}
          </Box>
          <Typography
            variant="bodySmall"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: "12px",
              fontWeight: 600,
            }}
          >
            {source.description}
          </Typography>
        </Box>
        {source.url && (
          <Typography
            variant="bodySmall"
            color="text.secondary"
            sx={{
              fontSize: "11px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {getDomain(source.url)}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const navButtonSx = {
  top: "50%",
  transform: "translateY(-50%)",
  backgroundColor: "rgba(255,255,255,0.15)",
  color: "white",
  zIndex: 10,
  padding: "10px",
  "&:hover": { backgroundColor: "rgba(255,255,255,0.25)" },
};

export default ({
  call,
  displayName,
}: {
  call: FunctionCall;
  displayName: string;
}) => {
  const isMobile = useIsMobile();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const sources = (call.sources || []) as BrowserUseSource[];
  const screenshotSources = sources.filter((s) => !!s.screenshot_url);
  const taskSummary = call.args?.task || "";
  const preview =
    taskSummary.length > 60 ? taskSummary.slice(0, 60) + "..." : taskSummary;

  const isOpen = selectedIndex !== null;
  const currentSource =
    selectedIndex !== null ? screenshotSources[selectedIndex] : null;
  const hasPrev = selectedIndex !== null && selectedIndex > 0;
  const hasNext =
    selectedIndex !== null && selectedIndex < screenshotSources.length - 1;

  const goNext = useCallback(() => {
    setSelectedIndex((i) =>
      i !== null && i < screenshotSources.length - 1 ? i + 1 : i,
    );
  }, [screenshotSources.length]);

  const goPrev = useCallback(() => {
    setSelectedIndex((i) => (i !== null && i > 0 ? i - 1 : i));
  }, []);

  const close = useCallback(() => setSelectedIndex(null), []);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") goNext();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, goNext, goPrev, close]);

  const openScreenshot = (source: BrowserUseSource) => {
    const idx = screenshotSources.indexOf(source);
    if (idx >= 0) setSelectedIndex(idx);
  };

  return (
    <>
      <DefaultActionContainer
        call={call}
        displayName={displayName}
        actionSummary={preview}
        defaultOpen={sources.length > 0 || call.state !== "completed"}
      >
        {call.state !== "completed" && taskSummary && (
          <Box sx={{ paddingLeft: "28px", paddingTop: "6px" }}>
            <Typography
              variant="bodySmall"
              color="text.secondary"
              sx={{ fontStyle: "italic" }}
            >
              {taskSummary}
            </Typography>
          </Box>
        )}
        {sources.length > 0 && (
          <Box
            sx={{
              paddingLeft: "28px",
              paddingTop: "8px",
              paddingBottom: "4px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: "8px",
                overflowX: "auto",
                paddingBottom: "4px",
                "&::-webkit-scrollbar": { height: "4px" },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#ccc",
                  borderRadius: "2px",
                },
              }}
            >
              {sources.map((source, index) => (
                <StepThumbnail
                  key={index}
                  source={source}
                  onClick={() => openScreenshot(source)}
                />
              ))}
            </Box>
          </Box>
        )}
      </DefaultActionContainer>

      <Dialog
        open={isOpen}
        onClose={close}
        fullScreen
        slotProps={{
          backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.85)" } },
        }}
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
        }}
      >
        <IconButton
          aria-label="close"
          onClick={close}
          sx={{
            position: "fixed",
            right: 24,
            top: 24,
            padding: "6px",
            backgroundColor: "rgba(255,255,255,0.15)",
            zIndex: 10,
            "&:hover": { backgroundColor: "rgba(255,255,255,0.25)" },
          }}
        >
          <CloseIcon sx={{ color: "white", fontSize: 20 }} />
        </IconButton>

        {hasPrev && (
          <IconButton
            aria-label="previous"
            onClick={goPrev}
            sx={{
              ...navButtonSx,
              position: "fixed",
              left: isMobile ? 12 : 24,
            }}
          >
            <ChevronLeft size={24} />
          </IconButton>
        )}

        {hasNext && (
          <IconButton
            aria-label="next"
            onClick={goNext}
            sx={{
              ...navButtonSx,
              position: "fixed",
              right: isMobile ? 12 : 24,
            }}
          >
            <ChevronRight size={24} />
          </IconButton>
        )}

        {currentSource && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "16px",
              maxWidth: "90vw",
              maxHeight: "90vh",
            }}
          >
            <img
              src={currentSource.screenshot_url}
              alt={currentSource.description}
              style={{
                maxWidth: isMobile ? "90vw" : "80vw",
                maxHeight: "75vh",
                objectFit: "contain",
                borderRadius: "8px",
              }}
            />
            <Box
              sx={{
                textAlign: "center",
                backgroundColor: "rgba(0,0,0,0.5)",
                borderRadius: "8px",
                padding: "8px 16px",
              }}
            >
              <Typography
                sx={{ color: "white", fontWeight: 600, fontSize: "14px" }}
              >
                {currentSource.description}
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.6)",
                  fontSize: "12px",
                  marginTop: "2px",
                }}
              >
                {currentSource.url
                  ? getDomain(currentSource.url) + " \u00B7 "
                  : ""}
                {(selectedIndex ?? 0) + 1} of {screenshotSources.length}
              </Typography>
            </Box>
          </Box>
        )}
      </Dialog>
    </>
  );
};
